import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Typography, Button, CircularProgress, Card, CardContent, Divider } from '@mui/material';
import { CheckCircleOutline, ErrorOutline } from '@mui/icons-material';
import Header from '../../../Reusable/Header';
import Footer from '../../../Reusable/Footer';
import baseURL from '../../user/config';

const COLORS = {
    primary: '#2D4A3D',
    text: '#1a1a2e',
    textSecondary: '#6b7280',
    border: '#e5e7eb',
    background: '#f9fafb',
};

const MembershipSuccess = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [status, setStatus] = useState(null); // 'success' | 'pending' | 'failed'
    const [application, setApplication] = useState(null);

    const params = new URLSearchParams(location.search);
    const reference = params.get('reference');
    const ref = params.get('ref');
    const applicationId = params.get('applicationId');

    useEffect(() => {
        const verify = async () => {
            try {
                if (reference) {
                    // Paystack redirect — verify by Paystack reference
                    const res = await fetch(`${baseURL}/membership/verify/${reference}`);
                    const data = await res.json();
                    if (data.status === 'success') {
                        setStatus('success');
                        setApplication(data.application);
                    } else {
                        setStatus('pending');
                    }
                } else if (ref) {
                    // Mobile Money — poll status by externalReference
                    const res = await fetch(`${baseURL}/membership/status/${ref}`);
                    const data = await res.json();
                    setStatus(data.status === 'Completed' ? 'success' : data.status === 'Failed' ? 'failed' : 'pending');
                    setApplication(data.application);
                } else if (applicationId) {
                    // PayPal — look up by applicationId
                    const res = await fetch(`${baseURL}/membership/status/${applicationId}`);
                    const data = await res.json();
                    setStatus(data.status === 'Completed' ? 'success' : data.status === 'Failed' ? 'failed' : 'pending');
                    setApplication(data.application);
                } else {
                    setStatus('failed');
                }
            } catch (err) {
                console.error('MembershipSuccess verify error:', err);
                setStatus('failed');
            } finally {
                setLoading(false);
            }
        };
        verify();
    }, [reference, ref, applicationId]);

    return (
        <div>
            <Header />
            <Box sx={{ backgroundColor: COLORS.background, minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center', py: 5 }}>
                <Box sx={{ maxWidth: 500, width: '100%', px: 2 }}>
                    {loading ? (
                        <Box sx={{ textAlign: 'center' }}>
                            <CircularProgress sx={{ color: COLORS.primary, mb: 2 }} />
                            <Typography sx={{ color: COLORS.textSecondary }}>Verifying your payment...</Typography>
                        </Box>
                    ) : (
                        <Card sx={{ borderRadius: '16px', boxShadow: '0 1px 6px rgba(0,0,0,0.1)', border: `1px solid ${COLORS.border}`, textAlign: 'center' }}>
                            <CardContent sx={{ p: 4 }}>
                                {status === 'success' ? (
                                    <>
                                        <CheckCircleOutline sx={{ fontSize: 64, color: '#2e7d32', mb: 2 }} />
                                        <Typography variant="h5" sx={{ fontWeight: 800, color: COLORS.text, mb: 1 }}>
                                            Membership Confirmed!
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: COLORS.textSecondary, mb: 3 }}>
                                            Thank you for joining. A confirmation has been sent to your email.
                                        </Typography>

                                        {application && (
                                            <>
                                                <Divider sx={{ mb: 2 }} />
                                                <Box sx={{ textAlign: 'left', mb: 2 }}>
                                                    {application.applicant_name && (
                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                                            <Typography variant="body2" sx={{ color: COLORS.textSecondary }}>Name</Typography>
                                                            <Typography variant="body2" sx={{ fontWeight: 600, color: COLORS.text }}>{application.applicant_name}</Typography>
                                                        </Box>
                                                    )}
                                                    {application.applicant_email && (
                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                                            <Typography variant="body2" sx={{ color: COLORS.textSecondary }}>Email</Typography>
                                                            <Typography variant="body2" sx={{ fontWeight: 600, color: COLORS.text }}>{application.applicant_email}</Typography>
                                                        </Box>
                                                    )}
                                                    {application.external_reference && (
                                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                                            <Typography variant="body2" sx={{ color: COLORS.textSecondary }}>Reference</Typography>
                                                            <Typography variant="body2" sx={{ fontWeight: 600, color: COLORS.text, fontSize: '0.78rem' }}>{application.external_reference}</Typography>
                                                        </Box>
                                                    )}
                                                </Box>
                                            </>
                                        )}
                                    </>
                                ) : status === 'pending' ? (
                                    <>
                                        <CircularProgress sx={{ color: COLORS.primary, mb: 2 }} size={48} />
                                        <Typography variant="h6" sx={{ fontWeight: 700, color: COLORS.text, mb: 1 }}>
                                            Payment Processing
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: COLORS.textSecondary, mb: 2 }}>
                                            Your payment is being processed. You'll receive a confirmation email once complete.
                                        </Typography>
                                    </>
                                ) : (
                                    <>
                                        <ErrorOutline sx={{ fontSize: 64, color: '#c62828', mb: 2 }} />
                                        <Typography variant="h6" sx={{ fontWeight: 700, color: COLORS.text, mb: 1 }}>
                                            Payment Not Confirmed
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: COLORS.textSecondary, mb: 2 }}>
                                            We couldn't confirm your payment. Please contact support if you were charged.
                                        </Typography>
                                    </>
                                )}

                                <Button
                                    variant="contained"
                                    onClick={() => navigate('/')}
                                    sx={{
                                        mt: 1,
                                        backgroundColor: COLORS.primary,
                                        '&:hover': { backgroundColor: '#1e3329' },
                                        borderRadius: '8px',
                                        textTransform: 'none',
                                        fontWeight: 700,
                                    }}
                                >
                                    Go to Home
                                </Button>
                            </CardContent>
                        </Card>
                    )}
                </Box>
            </Box>
            <Footer />
        </div>
    );
};

export default MembershipSuccess;
